/**
 * SoftMeter — GPS-based fare calculator.
 * Ported from the native Android SoftMeter.java / Meter.java / MeterDeserializer.java.
 *
 * Billing modes:
 *  - "distance" while vehicle speed >= threshold
 *  - "time"     after speed has been below threshold for > TIME_INTERVAL_MS (10 s)
 *
 * Fare math (canonical, matches DEVI999 dispatch):
 *  - flag drop:     cfg.flagRate (dollars, taken raw from server)
 *  - distance fare: floor(miles × divisor − includedUnits) × distance
 *                   where `distance` is DOLLARS per division (float, e.g. 0.50 = $0.50)
 *                   and `divisor` is divisions per mile
 *  - meter time:    floor(timeModeSec / cfg.timeUnitSeconds) × cfg.timeUnitRate
 *  - wait charge:   floor((waitSec − wait.includedUnits × wait.timeUnitSeconds) / wait.timeUnitSeconds)
 *                   × wait.timeUnitRate; raised to wait.minimumUnits × wait.timeUnitRate when > 0
 *  - minimum fare:  total = max(flag + dist + meterTime + wait, cfg.minimumFare) when > 0
 *
 * Wait timer ticks whenever speed is below `cfg.speedThreshold` (no 10 s buffer for wait —
 * the threshold directly gates wait per spec). Time-mode billing (`computeBlockTimeFare`)
 * still requires the 10 s below-threshold buffer (`_fareMode === 'time'`).
 *
 * The optional `meter.blockDistanceFare` (legacy fixed mile-block opt-in) is preserved
 * for fleets that ship config that way; otherwise the divisor/distance-cents math runs.
 *
 * Speed: GeolocationPosition.coords.speed is m/s; we convert to mph (×2.23694) or km/h
 * (×3.6) and compare to `cfg.speedThreshold` in the same units.
 */

const TIME_INTERVAL_MS = 10_000; // 10 s below threshold before time mode (Android TIME_INTERVAL_MS)
/** Cap GPS gap used for distance/speed and per-tick time billing (guards bad timestamps / sleep). */
const MAX_GPS_SEGMENT_DELTA_MS = 120_000;
/** Segment shorter than this (m) + below speed threshold ⇒ curb-style stop; timer may accrue wait between fixes. */
const WAIT_TIMER_MAX_SEGMENT_METERS = 32;
/** When Haversine displacement is at or below this (m), blend in speed×Δt (simulators / quantized GPS). */
const SEGMENT_GPS_NOISE_FLOOR_M = 2;
/** GPS-stationary wiggle: if the vehicle moves no more than this (m) over the elapsed window
 *  below, we treat it as parked and ignore any non-zero device-reported speed. Some chipsets
 *  (notably iOS in low-power mode and certain Android OEMs) report a stale/non-zero speed
 *  while the actual position has not changed, which would otherwise keep the meter in
 *  driving mode and starve the wait timer. */
const STATIONARY_WIGGLE_METERS = 8;
/** Minimum elapsed time before the GPS-stationary override kicks in. Avoids classifying a
 *  single sub-second fix as "parked". */
const STATIONARY_MIN_DT_MS = 2_000;
/** Max meters credited from speed alone per fix (guards bad timestamps). */
const SEGMENT_SPEED_FALLBACK_CAP_M = 400;
/** Hard cap on the distance attributed to any single GPS fix. Real driving at highway speed
 *  produces ~50 m/s; 200 m accommodates ~5 s GPS dropouts without billing teleports. Larger gaps
 *  must be reconciled by the OSRM gap-recovery path. */
const MAX_SINGLE_FIX_DISTANCE_M = 200;
/** Max ms of time/wait fare a single GPS fix may accrue. Caps catch-up after long sleeps. */
const MAX_TICK_ACCRUE_MS = 30_000;
const PERSIST_INTERVAL_MS = 30_000;
const STORAGE_KEY = 'softMeterState';

const MILES_PER_HOUR_FACTOR = 2.23694;
const KPH_FACTOR = 3.6;
const METERS_TO_MILES = 1609.344;
const METERS_TO_KM = 1000;
const MS_PER_HOUR = 3_600_000;

/** Trace buffer cap (in fixes) — Phase 4 OSRM Match flushes against this ring. */
const TRACE_BUFFER_MAX = 500;
/** Persist at most this many trace points across reloads. */
const TRACE_BUFFER_PERSIST_MAX = 200;

function haversineMeters(a, b) {
  if (
    !a ||
    !b ||
    !Number.isFinite(a.lat) ||
    !Number.isFinite(a.lng) ||
    !Number.isFinite(b.lat) ||
    !Number.isFinite(b.lng)
  ) {
    return 0;
  }
  const R = 6_371_000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinHalf = Math.sin(dLat / 2);
  const sinHalfLng = Math.sin(dLng / 2);
  const h =
    sinHalf * sinHalf +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinHalfLng * sinHalfLng;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function roundTwo(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Block-based time fare: floor(timeModeSec / cfg.timeUnitSeconds) × cfg.timeUnitRate.
 * Charges accrue immediately once the trip enters time mode (no free prefix on accrued
 * fare-time; the 10 s below-threshold mode-switch buffer is the only gating delay).
 */
export function computeBlockTimeFare(fareTimeSec, cfg) {
  const ft = Number(fareTimeSec);
  if (!Number.isFinite(ft) || ft <= 0) return 0;
  const blockSec = Number(cfg?.timeUnitSeconds) > 0 ? cfg.timeUnitSeconds : 60;
  const rate = Number(cfg?.timeUnitRate) || 0;
  if (blockSec <= 0 || rate <= 0) return 0;
  const blocks = Math.floor(ft / blockSec);
  return roundTwo(blocks * rate);
}

/**
 * Optional legacy fixed-mile-block distance fare (server flag `blockDistanceFare`).
 * Returns null when not configured — callers must fall back to divisor math.
 */
export function computeBlockDistanceFare(fareDistUnits, cfg, isMetric = false) {
  if (cfg.distanceBlockMiles == null || cfg.distanceBlockMiles <= 0) return null;
  if (cfg.distanceBlockRate == null || cfg.distanceBlockRate < 0) return null;
  let miles = Math.max(0, Number(fareDistUnits) || 0);
  if (isMetric) miles /= 1.609344;
  const blockMi = cfg.distanceBlockMiles;
  const freeMiles = (cfg.includedUnits || 0) * blockMi;
  const billMiles = Math.max(0, miles - freeMiles);
  const blocks = Math.floor(billMiles / blockMi);
  return roundTwo(blocks * cfg.distanceBlockRate);
}

/**
 * Canonical divisor distance fare:
 *   billable = floor(miles × divisor − includedUnits)
 *   fare     = max(0, billable) × distance
 * `distance` is DOLLARS per division (float, e.g. 0.50 = $0.50/division).
 */
export function computeDivisorDistanceFare(fareDistUnits, cfg, isMetric = false) {
  const div = Number(cfg?.distanceDivisor);
  const pricePerDivision = Number(cfg?.distancePricePerDivision);
  if (!Number.isFinite(div) || div <= 0) return 0;
  if (!Number.isFinite(pricePerDivision) || pricePerDivision <= 0) return 0;
  let miles = Math.max(0, Number(fareDistUnits) || 0);
  if (isMetric) miles /= 1.609344;
  const totalDivisions = miles * div;
  const free = Math.max(0, Number(cfg?.includedUnits) || 0);
  const billable = Math.max(0, Math.floor(totalDivisions - free));
  return roundTwo(billable * pricePerDivision);
}

function computeFarePortions(cfg, fareTimeSec, fareDistUnits, isMetric = false) {
  const roundedTime = computeBlockTimeFare(fareTimeSec, cfg);
  const blockDist = computeBlockDistanceFare(fareDistUnits, cfg, isMetric);
  const roundedDist =
    blockDist != null ? blockDist : computeDivisorDistanceFare(fareDistUnits, cfg, isMetric);
  return { roundedTime, roundedDist };
}

/**
 * GeolocationPosition.timestamp is specified as epoch ms; some clients report epoch seconds.
 * Wrong units make implied speed ~0 so the meter stays "stopped" while actually moving.
 */
function normalizeGeolocationTimestampMs(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return Date.now();
  if (n > 1e8 && n < 1e11) return Math.round(n * 1000);
  return Math.round(n);
}

/** Billable wait charge for accumulated wait milliseconds. */
function computeWaitChargeForMs(waitMs, waitConfig) {
  if (!waitConfig || waitConfig.timeUnitRate <= 0) return 0;
  const ms = Number(waitMs);
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  const wc = waitConfig;
  const waitSec = ms / 1000;
  const includedSec = (wc.includedUnits || 0) * wc.timeUnitSeconds;
  const netSec = Math.max(0, waitSec - includedSec);
  if (netSec <= 0) return 0;
  const blocks = Math.floor(netSec / wc.timeUnitSeconds);
  let charge = blocks * wc.timeUnitRate;
  const minCharge = (wc.minimumUnits || 0) * wc.timeUnitRate;
  if (charge > 0 && charge < minCharge) charge = minCharge;
  return roundTwo(charge);
}

/** Preview wait charge for UI (e.g. estimates from accumulated wait ms). */
export function previewWaitChargeMs(waitMs, waitConfig) {
  return computeWaitChargeForMs(waitMs, waitConfig);
}

/**
 * Speed implied by segment length / elapsed time, in the same units as `threshold`
 * (mph when !isMetric, km/h when isMetric). Used when browser `coords.speed` is missing.
 */
function impliedSpeedFromSegment(distanceMeters, dtMs, isMetric) {
  if (dtMs <= 0 || distanceMeters < 0) return Infinity;
  const kmh = (distanceMeters / 1000) / (dtMs / MS_PER_HOUR);
  if (isMetric) return kmh;
  return kmh * 0.621371;
}

/**
 * Odometer segment length: GPS displacement, or max(displacement, speed×Δt) when displacement
 * is within the noise floor (fake GPS / repeated coords with valid coords.speed in m/s).
 * Speed-derived growth is capped at SEGMENT_SPEED_FALLBACK_CAP_M to guard bad timestamps.
 */
function effectiveSegmentMeters(gpsMeters, speedMs, dtFixMs) {
  const gps = Math.max(0, Number(gpsMeters) || 0);
  if (gps > MAX_SINGLE_FIX_DISTANCE_M) {
    if (!effectiveSegmentMeters._warned) {
      console.warn(
        'SoftMeter: capped outlier fix distance',
        { gpsMeters: gps, capM: MAX_SINGLE_FIX_DISTANCE_M }
      );
      effectiveSegmentMeters._warned = true;
    }
    // eslint-disable-next-line no-console
    console.log('[METER] effectiveSegmentMeters CAPPED', {
      gpsMeters: Math.round(gps), capM: MAX_SINGLE_FIX_DISTANCE_M, dtFixMs,
    });
    return MAX_SINGLE_FIX_DISTANCE_M;
  }
  if (gps > SEGMENT_GPS_NOISE_FLOOR_M) return gps;
  const dtS = Number(dtFixMs) > 0 ? dtFixMs / 1000 : 0;
  let speedDerived = 0;
  if (Number.isFinite(speedMs) && speedMs >= 0 && dtS > 0) {
    speedDerived = Math.min(speedMs * dtS, SEGMENT_SPEED_FALLBACK_CAP_M);
  }
  return Math.max(gps, speedDerived);
}

/**
 * Parse a raw server "meter" JSON object into a normalized config.
 * Mirrors MeterDeserializer.java defaults.
 *
 * Field semantics:
 *  - flag                — flag drop in dollars (raw)
 *  - distance            — DOLLARS per division (float, e.g. 0.50 = $0.50)
 *  - divisor             — divisions per mile (e.g. 5 = 1/5 mi blocks)
 *  - includedUnits       — number of free divisions before billing starts
 *  - threshold           — speed threshold (mph default; km/h when isMetric)
 *  - timeUnitRate        — dollars per time block while in time mode
 *  - timeUnitSeconds     — block size in seconds
 *  - minimumFare         — dollars; raises the grand total when > 0
 *  - blockDistanceFare   — opt-in fixed-mile-block legacy mode
 */
export function parseMeterConfig(raw) {
  if (!raw) return null;

  const rawTsIn = raw.timeUnitSeconds ?? raw.TimeUnitSeconds;
  const rawTsNum = Number(rawTsIn);
  let timeUnitSeconds = Number.isFinite(rawTsNum) && rawTsNum > 0 ? rawTsNum : 60;
  if (!Number.isFinite(timeUnitSeconds) || timeUnitSeconds < 1) timeUnitSeconds = 60;
  // Common mistake: milliseconds sent as seconds (e.g. 60000 for 1 minute)
  if (timeUnitSeconds > 3600) {
    const asSec = Math.round(timeUnitSeconds / 1000);
    timeUnitSeconds = asSec >= 1 && asSec <= 3600 ? asSec : 60;
  }

  const rawTimeRateIn = Number(raw.timeUnitRate ?? raw.TimeUnitRate);
  const timeUnitRateNum =
    Number.isFinite(rawTimeRateIn) && rawTimeRateIn > 0 ? rawTimeRateIn : 0;

  const distancePricePerDivision = Number(raw.distance ?? raw.Distance) || 0;
  const div = Number(raw.divisor ?? raw.Divisor) || 1;

  // Opt-in legacy fixed-block-miles fare (server flag).
  let distanceBlockMiles = null;
  let distanceBlockRate = null;
  const blockOptIn =
    raw.blockDistanceFare === true ||
    raw.BlockDistanceFare === true ||
    Number(raw.distanceBlockMiles ?? raw.DistanceBlockMiles) > 0;
  if (blockOptIn) {
    let dbm = Number(raw.distanceBlockMiles ?? raw.DistanceBlockMiles);
    if (!Number.isFinite(dbm) || dbm <= 0) dbm = 0.2;
    let dbr = Number(raw.distanceBlockRate ?? raw.DistanceBlockRate);
    if (!Number.isFinite(dbr) || dbr < 0) dbr = 0.5;
    distanceBlockMiles = dbm;
    distanceBlockRate = dbr;
  }

  const flagDollars = Number(raw.flag ?? raw.Flag) || 0;

  return {
    id: raw.id || raw.Id || '',
    flagRate: flagDollars,
    distancePricePerDivision,
    // Aliases for back-compat with older callers/tests.
    distanceCentsPerDivision: distancePricePerDivision,
    distancePrice: distancePricePerDivision,
    distanceDivisor: div,
    distanceBlockMiles,
    distanceBlockRate,
    includedUnits: Number(raw.includedUnits ?? raw.IncludedUnits) || 0,
    speedThreshold: Number(raw.threshold ?? raw.Threshold) || 0,
    timeUnitRate: timeUnitRateNum,
    timeUnitSeconds: timeUnitSeconds === 0 ? 60 : timeUnitSeconds,
    minimumFare: Number(raw.minimumFare ?? raw.MinimumFare) || 0,
  };
}

/**
 * Parse a raw server "wait" JSON object into a normalized wait config.
 */
export function parseWaitConfig(raw) {
  if (!raw) return null;
  const rawSec =
    raw.timeUnitSeconds ?? raw.TimeUnitSeconds ?? raw.unitSeconds ?? raw.UnitSeconds;
  let timeUnitSeconds = Number(rawSec) > 0 ? Number(rawSec) : 60;
  if (!Number.isFinite(timeUnitSeconds) || timeUnitSeconds < 1) timeUnitSeconds = 60;
  if (timeUnitSeconds > 3600) {
    const asSec = Math.round(timeUnitSeconds / 1000);
    timeUnitSeconds = asSec >= 1 && asSec <= 3600 ? asSec : 60;
  }
  const timeUnitRate =
    Number(raw.timeUnitRate ?? raw.TimeUnitRate ?? raw.rate ?? raw.Rate) || 0;
  return {
    id: raw.id || raw.Id || '',
    timeUnitRate,
    timeUnitSeconds: timeUnitSeconds === 0 ? 60 : timeUnitSeconds,
    includedUnits: Number(raw.includedUnits ?? raw.IncludedUnits) || 0,
    minimumUnits: Number(raw.minimumUnits ?? raw.MinimumUnits) || 0,
  };
}

export default class SoftMeter {
  /**
   * When server sends threshold 0, comparisons like `speed < 0` never classify as stopped.
   * Use a small default (~5 mph / ~8 km/h) so wait and time-buffer logic still work.
   */
  static effectiveStopThreshold(config, isMetric) {
    const t = Number(config?.speedThreshold) || 0;
    if (t > 0) return t;
    return isMetric ? 8 : 5;
  }

  /**
   * @param {string}   jobNumber
   * @param {object}   config       Output of parseMeterConfig()
   * @param {object}   [opts]
   * @param {boolean}  [opts.isMetric=false]
   * @param {object}   [opts.waitConfig]    Output of parseWaitConfig()
   * @param {function} [opts.onFareUpdate]  (fare, fareMode, totalDistance, waitCharge) => void
   * @param {function} [opts.onMeterTick]   (meterStats) => void
   * @param {function} [opts.onTraceFlush]  (points: {lat,lng,ts,accuracy?}[]) => Promise<{matchedDistanceMeters?:number}|void>
   *                                         Optional async hook for OSRM Match — when supplied the
   *                                         meter calls it every TRACE_FLUSH_INTERVAL_MS / TRACE_BUFFER_FLUSH_AT
   *                                         points and replaces the raw segment distance with the matched value.
   */
  constructor(jobNumber, config, opts = {}) {
    this._jobNumber = jobNumber;
    this._config = config;
    this._waitConfig = opts.waitConfig || null;
    this._isMetric = opts.isMetric ?? false;
    this._onFareUpdate = opts.onFareUpdate || null;
    this._onMeterTick = opts.onMeterTick || null;
    this._onTraceFlush = typeof opts.onTraceFlush === 'function' ? opts.onTraceFlush : null;

    this._speedFactor = this._isMetric ? KPH_FACTOR : MILES_PER_HOUR_FACTOR;
    this._distDivisor = this._isMetric ? METERS_TO_KM : METERS_TO_MILES;
    this._stopSpeedThreshold = SoftMeter.effectiveStopThreshold(config, this._isMetric);

    this._enabled = false;
    this._sessionStart = 0;
    this._lastTimestamp = 0;
    this._lastLocation = null;
    this._timeBufferCounter = 0;

    /** Total odometer (meters) — Haversine + speed fallback; replaced piece-wise by OSRM Match when available. */
    this._totalJobDistance = 0;
    /** Raw odometer kept for debug/telemetry; only matched output goes to billing in flush mode. */
    this._rawHaversineDistance = 0;
    this._fareDistance = 0;
    this._fareTime = 0;
    this._fare = 0;
    this._fareMode = 'distance';
    this._broadcastFare = true;

    this._waitTimeMs = 0;
    this._isWaiting = false;
    this._waitCharge = 0;
    this._distanceCharge = 0;
    this._timeCharge = 0;

    this._timerHandle = null;
    this._lastPersistTime = 0;
    this._lastEmittedDisplayDistance = null;
    this._lastEmittedOdometerMeters = null;

    this._lastFixTimestamp = 0;
    /** Wall-clock ms when the previous GPS fix was processed. Used to ensure wait/idle
     *  accrue real elapsed time even when the chipset returns cached fixes with stale
     *  timestamps. */
    this._lastWallClockTs = 0;
    this._lastMotionBelowThreshold = false;
    this._lastSegmentStationaryForWaitTimer = false;
    this._lastEffectiveSpeed = 0;
    this._sessionFirstFixTs = null;

    /** Ring buffer of recent fixes for OSRM Match / Route gap recovery. */
    this._traceBuffer = [];
    /** Last point captured before app went background (visibilitychange → hidden). */
    this._lastValidPointBeforeGap = null;
    /** Set by onPause() and cleared by the first post-resume fix; signals the in-line accumulator
     *  to skip distance/time/wait accrual for that fix so the OSRM gap-recovery path can replace
     *  the segment without double counting. */
    this._gapPending = false;
    /** First fix observed after a gap; used by applyMatchedDistance() to compute window meters. */
    this._postGapAnchor = null;
  }

  // ── public API ─────────────────────────────────────────────

  get jobNumber() { return this._jobNumber; }
  get enabled()   { return this._enabled; }
  get fare()      { return this._fare; }
  get fareMode()  { return this._fareMode; }
  get isWaiting() { return this._isWaiting; }
  get waitTimeSeconds() {
    return Math.round((this._waitTimeMs + this._projectedWaitMs()) / 1000);
  }
  get waitCharge() {
    // Recompute live so the WAIT $ display ticks every second once the included
    // free wait window is exceeded, even between GPS fixes.
    if (this._waitConfig && this._waitConfig.timeUnitRate > 0) {
      return computeWaitChargeForMs(
        this._waitTimeMs + this._projectedWaitMs(),
        this._waitConfig
      );
    }
    return this._waitCharge;
  }
  /** Live distance charge for the meter strip DIST column. Last computed by calculateFare(). */
  get distanceCharge() { return this._distanceCharge; }
  /** Live time charge for the meter strip TIME column. Last computed by calculateFare(). */
  get timeCharge() { return this._timeCharge; }
  get fareTimeSeconds() {
    return Math.max(
      0,
      Math.round((this._fareTime + this._projectedFareTimeMs()) / 1000)
    );
  }
  get elapsedSeconds() {
    if (this._sessionStart === 0) return 0;
    const wallEnd = this._enabled ? Date.now() : this._lastTimestamp;
    const wallMs = wallEnd - this._sessionStart;
    const gpsMs =
      this._sessionFirstFixTs != null && this._lastTimestamp >= this._sessionFirstFixTs
        ? this._lastTimestamp - this._sessionFirstFixTs
        : 0;
    return Math.max(0, Math.round(Math.max(wallMs, gpsMs) / 1000));
  }

  /**
   * Wall-clock ms accrued since the last GPS fix, ONLY counted if the meter's
   * current classification says we're in a wait-eligible state. Capped to
   * MAX_TICK_ACCRUE_MS so a backgrounded tab doesn't snap-credit minutes.
   * The next real GPS fix will accrue exactly this same delta into _waitTimeMs,
   * so the displayed value stays continuous (no jump-back).
   */
  _projectedWaitMs() {
    if (!this._enabled) return 0;
    if (!this._isWaiting) return 0;
    if (!this._lastWallClockTs) return 0;
    const delta = Date.now() - this._lastWallClockTs;
    if (delta <= 0) return 0;
    return Math.min(delta, MAX_TICK_ACCRUE_MS);
  }

  /**
   * Wall-clock ms to project onto the running time-mode (idle) fare since the
   * last GPS fix. Mirrors _projectedWaitMs but gated on _fareMode === 'time'.
   * Also projects when the meter rate has a concurrent timeUnitRate component
   * (two-tariff $/mi + $/min model) — in that case the time fare ticks every
   * second regardless of whether the vehicle is moving.
   */
  _projectedFareTimeMs() {
    if (!this._enabled) return 0;
    const concurrentTime =
      this._config && Number(this._config.timeUnitRate) > 0;
    if (!concurrentTime && this._fareMode !== 'time') return 0;
    if (!this._lastWallClockTs) return 0;
    const delta = Date.now() - this._lastWallClockTs;
    if (delta <= 0) return 0;
    return Math.min(delta, MAX_TICK_ACCRUE_MS);
  }

  get totalDistance() {
    return roundTwo(this._totalJobDistance / this._distDivisor);
  }

  /** Snapshot of the trace ring buffer (caller-owned copy). */
  getTraceBuffer() {
    return this._traceBuffer.slice();
  }

  /** Apply a matched distance for a window of buffered points (called by OSRM helper).
   *  When `windowMeters` is omitted/zero AND a post-gap anchor is available, the window is computed
   *  from `haversine(lastValidPointBeforeGap, postGapAnchor)` so the matched distance REPLACES the
   *  gap segment rather than adding to it. */
  applyMatchedDistance(matchedMeters, windowMeters) {
    const m = Number(matchedMeters);
    let raw = Number(windowMeters);
    if (!Number.isFinite(m) || m < 0) return;
    if (!Number.isFinite(raw) || raw <= 0) {
      // Try to derive the window from the captured gap anchors so this becomes a delta correction.
      if (this._lastValidPointBeforeGap && this._postGapAnchor) {
        raw = haversineMeters(this._lastValidPointBeforeGap, this._postGapAnchor);
      }
    }
    if (!Number.isFinite(raw) || raw <= 0) {
      // No window known and no anchors — fall back to additive (kept for callers that don't gap).
      this._totalJobDistance += m;
      this._fareDistance += m;
      this._postGapAnchor = null;
      return;
    }
    const delta = m - raw;
    this._totalJobDistance = Math.max(0, this._totalJobDistance + delta);
    this._fareDistance = Math.max(0, this._fareDistance + delta);
    this._postGapAnchor = null;
  }

  /** Apply a gap-recovery distance ADDITIVELY (no delta math).
   *  Use this when the gap segment was skipped entirely by `onLocationUpdate` (the post-gap
   *  branch returns early before any haversine is accrued). The matched distance must be
   *  ADDED in full — there is nothing to "replace". Also clears post-gap anchors and
   *  bumps the persisted snapshot. */
  applyGapDistance(matchedMeters) {
    const m = Number(matchedMeters);
    if (!Number.isFinite(m) || m <= 0) {
      this._postGapAnchor = null;
      this._lastValidPointBeforeGap = null;
      return;
    }
    // Sanity cap: never credit more than 50 km from a single gap recovery.
    const credit = Math.min(m, 50_000);
    this._totalJobDistance += credit;
    this._fareDistance += credit;
    this._postGapAnchor = null;
    this._lastValidPointBeforeGap = null;
    // eslint-disable-next-line no-console
    console.log('[METER] softMeter applyGapDistance', {
      creditedM: Math.round(credit),
      totalJobMetersAfter: Math.round(this._totalJobDistance * 100) / 100,
    });
    this._maybePersist();
  }

  /**
   * Credit BOTH distance and time for a verified background gap.
   *
   * `applyGapDistance` only adds road distance — but during the background period the
   * vehicle was either driving (no wait/time charge) or sitting (full wait/time charge),
   * or (most commonly for long gaps) BOTH. Without this call, idle background time
   * produces $0 of wait charge even though the driver was waiting with the meter running.
   *
   * Splitting model:
   *   - When `expectedDurationSec` is provided (from OSRM Route), the gap is split:
   *       drivingMs  = min(gapMs, expectedDurationMs)        // free-flow OSRM travel time
   *       waitingMs  = max(0, gapMs − expectedDurationMs)    // remainder is wait
   *     This mirrors the user's intent: the app reconciles missed background time by
   *     trusting OSRM's expected travel duration for the road distance covered, and
   *     billing the remainder as wait.
   *   - When `expectedDurationSec` is missing (OSRM Route failed → haversine fallback),
   *     fall back to the implied-speed binary heuristic:
   *       speed >= threshold  → all driving
   *       speed <  threshold  → all waiting
   *
   * @param {number} matchedMeters         Road distance for the gap (from OSRM Route or haversine).
   * @param {number} gapDurationMs         Wall-clock duration of the gap (after.ts − anchor.ts).
   * @param {number} [expectedDurationSec] Free-flow travel time for `matchedMeters` (OSRM Route).
   */
  applyGapInterval(matchedMeters, gapDurationMs, expectedDurationSec) {
    const m = Number(matchedMeters);
    const dt = Number(gapDurationMs);
    if (!Number.isFinite(dt) || dt <= 0) {
      // Just credit distance; no time data to attribute.
      this.applyGapDistance(m);
      return;
    }

    // Sanity cap: never credit more than 1 hour of wait/time from a single gap recovery
    // (guards against absurd persisted-state timestamps).
    const cappedDt = Math.min(dt, 3_600_000);
    const meters = Number.isFinite(m) && m > 0 ? Math.min(m, 50_000) : 0;

    let drivingMs = 0;
    let waitingMs = 0;
    let mode = 'unknown';

    const expectedMs =
      Number.isFinite(expectedDurationSec) && expectedDurationSec > 0
        ? Math.min(expectedDurationSec * 1000, cappedDt)
        : 0;

    if (expectedMs > 0) {
      // OSRM-driven split: trust expected travel duration for the matched road distance,
      // and bill the remainder as wait. This is the canonical path for the user's
      // background-gap reconciliation flow.
      drivingMs = expectedMs;
      waitingMs = Math.max(0, cappedDt - expectedMs);
      mode = 'osrm-split';
    } else {
      // Fallback: implied-speed binary heuristic when OSRM duration is unavailable.
      const impliedMs = meters / (cappedDt / 1000);
      const impliedSpeed = impliedMs * this._speedFactor;
      if (impliedSpeed >= this._stopSpeedThreshold) {
        drivingMs = cappedDt;
        waitingMs = 0;
        mode = 'fallback-driving';
      } else {
        drivingMs = 0;
        waitingMs = cappedDt;
        mode = 'fallback-waiting';
      }
    }

    let waitAddedMs = 0;
    let timeFareAddedMs = 0;

    if (waitingMs > 0) {
      // Idle background portion → credit wait clock (and time-mode fare once buffer is exceeded).
      this._waitTimeMs += waitingMs;
      this._isWaiting = true;
      waitAddedMs = waitingMs;

      // Time-mode billing requires the 10 s buffer. The buffer "fills" first, then any
      // waiting beyond the buffer bills as time-mode fare.
      const bufferRemaining = Math.max(0, TIME_INTERVAL_MS - this._timeBufferCounter);
      const bufferConsumed = Math.min(bufferRemaining, waitingMs);
      const timeModeMs = waitingMs - bufferConsumed;
      this._timeBufferCounter += waitingMs;
      if (timeModeMs > 0) {
        this._fareTime += timeModeMs;
        this._fareMode = 'time';
        timeFareAddedMs = timeModeMs;
      }
    }

    if (drivingMs > 0 && waitingMs === 0) {
      // Pure-driving gap: reset the time buffer so the next stationary segment starts fresh.
      this._timeBufferCounter = 0;
      this._isWaiting = false;
      this._fareMode = 'distance';
    }

    // Credit the road distance + clear the gap anchors.
    this.applyGapDistance(meters);

    // eslint-disable-next-line no-console
    console.log('[METER] softMeter applyGapInterval', {
      mode,
      gapSec: Math.round(cappedDt / 1000),
      matchedM: Math.round(meters),
      expectedDurationSec: Math.round((expectedDurationSec || 0) * 10) / 10,
      drivingSec: Math.round(drivingMs / 1000),
      waitingSec: Math.round(waitingMs / 1000),
      waitFareMsAdded: waitAddedMs,
      timeFareMsAdded: timeFareAddedMs,
      totalWaitSec: this.waitTimeSeconds,
      totalFareTimeSec: Math.round(this._fareTime / 1000),
    });

    // Recompute fare so wait charge and time-mode fare reflect the new totals.
    this.calculateFare(false);
    this._persistState();
  }

  enable() {
    this.disable();
    const now = Date.now();
    const isNewSession = this._sessionStart === 0;
    if (isNewSession) {
      this._sessionStart = now;
      this._lastTimestamp = now;
      this._sessionFirstFixTs = null;
    }
    this._enabled = true;
    this._broadcastFare = true;
    this._lastEmittedDisplayDistance = null;
    this._lastEmittedOdometerMeters = null;
    this._lastFixTimestamp = 0;
    this._lastWallClockTs = 0;
    this._lastMotionBelowThreshold = false;
    this._lastSegmentStationaryForWaitTimer = false;
    this._lastEffectiveSpeed = 0;
    this._startTimer();
    this.calculateFare(false);
    this._persistState();
  }

  disable() {
    if (this._enabled && this._sessionStart > 0) {
      this._lastTimestamp = Date.now();
    }
    this._enabled = false;
    this._broadcastFare = false;
    this._lastLocation = null;
    this._lastEmittedOdometerMeters = null;
    this._stopTimer();
    SoftMeter.clearPersistedState();
  }

  /**
   * Feed a new GPS position into the meter.
   */
  onLocationUpdate(lat, lng, speedMs, timestamp) {
    if (!this._enabled) {
      // eslint-disable-next-line no-console
      console.log('[METER] softMeter.onLocationUpdate dropped — meter disabled');
      return;
    }
    const la = Number(lat);
    const lo = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(lo) || Math.abs(la) > 90 || Math.abs(lo) > 180) {
      // eslint-disable-next-line no-console
      console.log('[METER] softMeter.onLocationUpdate dropped — invalid coords', { lat, lng });
      return;
    }

    let ts = normalizeGeolocationTimestampMs(timestamp);
    if (this._lastLocation && ts <= this._lastTimestamp) {
      ts = this._lastTimestamp + 1;
    }

    if (this._sessionFirstFixTs == null) {
      this._sessionFirstFixTs = ts;
    }

    const priorHadLocation = !!this._lastLocation;
    let fareMode = 'distance';
    let effectiveSpeed = 0;

    if (this._lastLocation) {
      const rawDtFix = this._lastFixTimestamp > 0 ? ts - this._lastFixTimestamp : 0;
      const dtFix = Math.min(Math.max(0, rawDtFix), MAX_GPS_SEGMENT_DELTA_MS);
      const tickRaw = ts > this._lastTimestamp ? ts - this._lastTimestamp : 0;
      // Wall-clock delta since the previous processed fix. Some chipsets / cached GPS
      // calls return positions with stale `position.timestamp` (or duplicates of the
      // previous fix), which would collapse `tickRaw` to ~1 ms and starve the wait /
      // idle clocks even though wall-clock seconds are passing. Take the larger of the
      // two so wait & idle reflect real elapsed time. Distance accrual still uses the
      // GPS-based `dtFix` so we never inflate odometer from cached-fix replays.
      const wallTickRaw =
        this._lastWallClockTs > 0
          ? Math.max(0, Date.now() - this._lastWallClockTs)
          : 0;
      // Cap the per-fix time/wait contribution; longer real-world gaps must come through the
      // OSRM gap-recovery path, not via MAX_GPS_SEGMENT_DELTA_MS catch-up.
      const tickDt = Math.min(Math.max(tickRaw, wallTickRaw), MAX_TICK_ACCRUE_MS);

      // First fix after a gap: skip distance / time / wait accrual entirely so the OSRM
      // gap-recovery path can REPLACE the segment via applyMatchedDistance(matched, windowM).
      // Still update _lastLocation/_lastFixTimestamp so subsequent fixes resume normally.
      if (this._gapPending) {
        // eslint-disable-next-line no-console
        console.log('[METER] softMeter post-gap fix — SKIPPING distance/time accrual (awaiting OSRM Route gap recovery)', {
          lat: la, lng: lo, ts, gapAnchor: this._lastValidPointBeforeGap,
        });
        this._gapPending = false;
        this._postGapAnchor = { lat: la, lng: lo, ts };
        this._lastFixTimestamp = ts;
        this._lastWallClockTs = Date.now();
        this._lastLocation = { lat: la, lng: lo };
        this._lastTimestamp = ts;
        this._lastEffectiveSpeed = 0;
        this._fareMode = 'distance';
        this._timeBufferCounter = 0;
        this._pushTrace(la, lo, ts, speedMs);
        this._updateMeter(0, 'distance');
        this._maybePersist();
        return;
      }

      const gpsMeters = haversineMeters(this._lastLocation, { lat: la, lng: lo });
      let distance = effectiveSegmentMeters(gpsMeters, speedMs, dtFix);
      // eslint-disable-next-line no-console
      console.log('[METER] softMeter segment', {
        gpsMeters: Math.round(gpsMeters * 100) / 100,
        effectiveMeters: Math.round(distance * 100) / 100,
        dtFixMs: dtFix,
        tickDtMs: tickDt,
        speedMs,
        totalJobMetersBefore: Math.round(this._totalJobDistance * 100) / 100,
      });

      const implied = impliedSpeedFromSegment(distance, dtFix, this._isMetric);
      const deviceMphOrKmh =
        Number.isFinite(speedMs) && speedMs >= 0 ? speedMs * this._speedFactor : NaN;
      effectiveSpeed =
        Number.isFinite(deviceMphOrKmh) && deviceMphOrKmh > 0
          ? Math.max(implied, deviceMphOrKmh)
          : implied;

      // GPS-stationary override: if actual displacement is within the wiggle threshold
      // over a meaningful window, the vehicle is parked regardless of what the device
      // chip reports. Some platforms emit stale non-zero speed while position is frozen,
      // which would otherwise pin the meter in distance mode and prevent wait accrual.
      // We must also clamp `distance` back to the real Haversine displacement, otherwise
      // the speed×Δt blend in effectiveSegmentMeters() can synthesize a >32 m segment
      // from a stale speed, which would (a) inflate the odometer and (b) trip the
      // distance < WAIT_TIMER_MAX_SEGMENT_METERS guard and prevent wait accrual.
      if (
        gpsMeters <= STATIONARY_WIGGLE_METERS &&
        dtFix >= STATIONARY_MIN_DT_MS
      ) {
        if (effectiveSpeed > 0 || distance > gpsMeters) {
          // eslint-disable-next-line no-console
          console.log('[METER] softMeter GPS-stationary override — clamping speed & distance', {
            gpsMeters: Math.round(gpsMeters * 100) / 100,
            dtFixMs: dtFix,
            deviceMphOrKmh,
            impliedBefore: implied,
            effectiveSpeedBefore: effectiveSpeed,
            distanceBefore: Math.round(distance * 100) / 100,
          });
        }
        effectiveSpeed = 0;
        distance = gpsMeters;
      }

      this._totalJobDistance += distance;
      this._rawHaversineDistance += gpsMeters;

      const belowThreshold = effectiveSpeed < this._stopSpeedThreshold;
      this._lastMotionBelowThreshold = belowThreshold;

      const stationaryForWait =
        belowThreshold && distance < WAIT_TIMER_MAX_SEGMENT_METERS;
      this._lastSegmentStationaryForWaitTimer = stationaryForWait;

      // Time mode (separate, billable time-fare) begins once the 10s below-threshold buffer is exceeded.
      const inTimeMode = belowThreshold && this._timeBufferCounter > TIME_INTERVAL_MS;

      // Wait clock ticks immediately whenever the vehicle is below the speed threshold and the
      // segment is short enough to be a real stop (not slow GPS while driving). The 10s time-mode
      // buffer does NOT gate wait — per spec: "the threshold indicates the speed where the
      // 'meter' rate is no longer active and the 'waiting' rate is active."
      const accrueWaitTime =
        belowThreshold &&
        effectiveSpeed <= this._stopSpeedThreshold &&
        distance < WAIT_TIMER_MAX_SEGMENT_METERS;

      if (accrueWaitTime && tickDt > 0) {
        this._waitTimeMs += tickDt;
        this._isWaiting = true;
      } else {
        this._isWaiting = false;
      }

      // Concurrent meter time accrual: when the rate config has a non-zero
      // timeUnitRate, the meter's time component is a CONCURRENT charge that
      // ticks throughout the trip independently of the distance component
      // (standard two-tariff "$/mi + $/min" meter model). It is NOT mutually
      // exclusive with distance, and it accrues regardless of whether wait
      // is also configured. The fareMode display flag below still reflects
      // the dominant accrual (distance vs. idle time-mode) for the UI.
      const hasMeterTimeComponent =
        this._config && Number(this._config.timeUnitRate) > 0;
      if (hasMeterTimeComponent && tickDt > 0) {
        this._fareTime += tickDt;
        if (ts > this._lastTimestamp) this._lastTimestamp = ts;
      }

      if (belowThreshold) {
        if (inTimeMode) {
          fareMode = 'time';
          if (tickDt > 0) {
            this._timeBufferCounter += tickDt;
            // _fareTime already accrued above when hasMeterTimeComponent.
            // Only call _incrementTime when there's no meter time component
            // so we still update _lastTimestamp / restart the idle timer.
            if (!hasMeterTimeComponent) {
              this._incrementTime(ts);
            } else if (ts > this._lastTimestamp) {
              this._lastTimestamp = ts;
              this._restartTimer();
            }
          }
        } else {
          if (tickDt > 0) {
            this._timeBufferCounter += tickDt;
            this._incrementDistance(distance, ts);
          }
        }
      } else {
        this._timeBufferCounter = 0;
        this._incrementDistance(distance, ts);
      }
    }

    this._lastFixTimestamp = ts;
    this._lastWallClockTs = Date.now();
    this._lastLocation = { lat: la, lng: lo };
    this._lastEffectiveSpeed = effectiveSpeed;
    this._fareMode = fareMode;
    this._pushTrace(la, lo, ts, speedMs);
    if (!priorHadLocation) {
      this._lastTimestamp = ts;
    }
    this._updateMeter(effectiveSpeed, fareMode);
    this._maybePersist();
  }

  /**
   * Compute the fare. Pass `true` for the final trip fare (applies minimum to total).
   */
  calculateFare(isFinal = false) {
    const cfg = this._config;

    const fareTimeSec = this._fareTime / 1000;
    const fareDistUnits = this._fareDistance / this._distDivisor;

    const { roundedTime, roundedDist } = computeFarePortions(
      cfg,
      fareTimeSec,
      fareDistUnits,
      this._isMetric
    );

    this._waitCharge = this._calculateWaitCharge();

    // Cache the per-component charges so the live UI can show them in the meter
    // strip (DIST / TIME / WAIT columns each show their own running charge).
    this._distanceCharge = roundedDist;
    this._timeCharge = roundedTime;

    let fare = roundTwo(roundedTime + roundedDist + cfg.flagRate + this._waitCharge);

    if (isFinal && cfg.minimumFare > 0 && fare < cfg.minimumFare) {
      fare = cfg.minimumFare;
    }

    const displayDist = this.totalDistance;
    const prevFare = this._fare;
    const prevEmittedDist = this._lastEmittedDisplayDistance;
    this._fare = fare;

    const fareMoved = Math.abs(fare - prevFare) >= 0.001;
    const distMoved =
      prevEmittedDist == null || Math.abs(displayDist - prevEmittedDist) >= 0.001;
    const odometerMoved =
      this._lastEmittedOdometerMeters == null ||
      Math.abs(this._totalJobDistance - this._lastEmittedOdometerMeters) >= 1;
    if (this._broadcastFare && (fareMoved || distMoved || odometerMoved)) {
      this._lastEmittedDisplayDistance = displayDist;
      this._lastEmittedOdometerMeters = this._totalJobDistance;
      this._emitFareUpdate();
    }

    if (fareMoved) {
      // eslint-disable-next-line no-console
      console.log('[METER] fare changed — breakdown', {
        jobNumber: this._jobNumber,
        prevFare: roundTwo(prevFare),
        newFare: roundTwo(fare),
        delta: roundTwo(fare - prevFare),
        flagRate: roundTwo(cfg.flagRate),
        distanceCharge: roundTwo(roundedDist),
        timeCharge: roundTwo(roundedTime),
        waitCharge: roundTwo(this._waitCharge),
        minimumFare: roundTwo(cfg.minimumFare || 0),
        fareMode: this._fareMode,
        isWaiting: this._isWaiting,
        waitSeconds: this.waitTimeSeconds,
        fareTimeSec: Math.round(this._fareTime / 1000),
        fareDistanceUnits: roundTwo(this._fareDistance / this._distDivisor),
        totalDistance: displayDist,
        isFinal,
      });
    }

    return roundTwo(fare);
  }

  /** Build fare breakdown for the confirmation modal. Minimum fare is centralized in `total`. */
  getFareBreakdown(isFinal = true) {
    const cfg = this._config;
    const fareTimeSec = this._fareTime / 1000;
    const fareDistUnits = this._fareDistance / this._distDivisor;

    const { roundedTime, roundedDist } = computeFarePortions(
      cfg,
      fareTimeSec,
      fareDistUnits,
      this._isMetric
    );
    const waitCharge = this._calculateWaitCharge();

    const baseTotal = roundTwo(cfg.flagRate + roundedDist + roundedTime + waitCharge);
    const minFare = Number(cfg.minimumFare) || 0;
    const total = isFinal && minFare > 0 && baseTotal < minFare ? roundTwo(minFare) : baseTotal;

    return {
      flagRate: roundTwo(cfg.flagRate),
      distanceCharge: roundTwo(roundedDist),
      timeCharge: roundTwo(roundedTime),
      waitCharge: roundTwo(waitCharge),
      total,
      distance: this.totalDistance,
      elapsedSeconds: this.elapsedSeconds,
      waitSeconds: this.waitTimeSeconds,
      minimumFare: minFare,
    };
  }

  /** Gather stats for the METER socket message. */
  getMeterStats(gpsSpeed) {
    const spd = Number.isFinite(gpsSpeed) ? gpsSpeed : this._lastEffectiveSpeed;
    return {
      jobNumber: this._jobNumber,
      totalDistance: roundTwo(this._totalJobDistance / this._distDivisor),
      totalTime: this.elapsedSeconds,
      distanceFare: roundTwo(this._fareDistance / this._distDivisor),
      timeFare: Math.round(this._fareTime / 1000),
      gpsSpeed: Math.round(spd),
      fareMode: this._fareMode,
      waitTime: this.waitTimeSeconds,
      waitCharge: this._waitCharge,
    };
  }

  // ── trace buffer ───────────────────────────────────────────

  _pushTrace(lat, lng, ts, speedMs) {
    const entry = { lat, lng, ts };
    if (Number.isFinite(speedMs)) entry.speed = speedMs;
    this._traceBuffer.push(entry);
    if (this._traceBuffer.length > TRACE_BUFFER_MAX) {
      this._traceBuffer.splice(0, this._traceBuffer.length - TRACE_BUFFER_MAX);
    }
  }

  /** Snapshot last point before background; restore via `onResume()`. */
  onPause() {
    if (this._lastLocation && Number.isFinite(this._lastFixTimestamp)) {
      this._lastValidPointBeforeGap = {
        lat: this._lastLocation.lat,
        lng: this._lastLocation.lng,
        ts: this._lastFixTimestamp,
      };
      this._gapPending = true;
      this._postGapAnchor = null;
      this._persistState();
    }
  }

  /** Returns the snapshotted gap point (consumed once) or null. */
  consumeGapAnchor() {
    const a = this._lastValidPointBeforeGap;
    this._lastValidPointBeforeGap = null;
    return a;
  }

  // ── localStorage persistence ───────────────────────────────

  _persistState() {
    // Hard guard: a disabled meter must never write to storage. disable() clears
    // the snapshot on trip completion; any deferred callback (timer tick, async
    // OSRM result, visibility flush, gap handler) that fires after disable would
    // otherwise resurrect the snapshot and trigger an auto-resume on next mount.
    if (!this._enabled) return;
    try {
      // Persist a bounded tail of the trace buffer so OSRM Match can replay on reload.
      const trace =
        this._traceBuffer.length > TRACE_BUFFER_PERSIST_MAX
          ? this._traceBuffer.slice(this._traceBuffer.length - TRACE_BUFFER_PERSIST_MAX)
          : this._traceBuffer.slice();

      const data = {
        jobNumber: this._jobNumber,
        config: this._config,
        waitConfig: this._waitConfig,
        isMetric: this._isMetric,
        sessionStart: this._sessionStart,
        lastTimestamp: this._lastTimestamp,
        totalJobDistance: this._totalJobDistance,
        rawHaversineDistance: this._rawHaversineDistance,
        fareDistance: this._fareDistance,
        fareTime: this._fareTime,
        fare: this._fare,
        waitTimeMs: this._waitTimeMs,
        lastLocation: this._lastLocation,
        timeBufferCounter: this._timeBufferCounter,
        lastFixTimestamp: this._lastFixTimestamp,
        lastMotionBelowThreshold: this._lastMotionBelowThreshold,
        lastSegmentStationaryForWaitTimer: this._lastSegmentStationaryForWaitTimer,
        lastEffectiveSpeed: this._lastEffectiveSpeed,
        sessionFirstFixTs: this._sessionFirstFixTs,
        lastValidPointBeforeGap: this._lastValidPointBeforeGap,
        gapPending: this._gapPending,
        postGapAnchor: this._postGapAnchor,
        traceBuffer: trace,
        persistedAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      this._lastPersistTime = Date.now();
    } catch (e) {
      console.warn('SoftMeter: failed to persist state', e);
    }
  }

  _maybePersist() {
    if (Date.now() - this._lastPersistTime >= PERSIST_INTERVAL_MS) {
      this._persistState();
    }
  }

  static clearPersistedState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  }

  static getPersistedState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.persistedAt > 4 * 60 * 60 * 1000) {
        SoftMeter.clearPersistedState();
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  /**
   * True when persisted state exists AND its jobNumber matches `jobNumber` (string-compared,
   * trimmed). Used by the hook so STARTM-replay after a reload resumes the same flag trip
   * instead of stomping accumulated totals with a fresh meter.
   */
  static hasPersistedStateFor(jobNumber) {
    const saved = SoftMeter.getPersistedState();
    if (!saved) return false;
    const a = String(saved.jobNumber ?? '').trim();
    const b = String(jobNumber ?? '').trim();
    return a !== '' && a === b;
  }

  /**
   * Tear down the live in-memory meter (stop timer, drop runtime callbacks) WITHOUT
   * clearing localStorage. Used by the React hook's mount-effect cleanup so a transient
   * unmount (background suspend, hot-reload, parent re-render) doesn't wipe the trip's
   * accumulated progress — the next mount restores via `getPersistedState()`.
   */
  detachInMemory() {
    // Only re-persist when the meter is still live. If it was already disabled
    // (e.g. via stopMeter() on trip completion), persisting would resurrect the
    // localStorage snapshot that disable() just cleared, and the next mount
    // would auto-resume the completed trip. See useSoftMeter mount-effect.
    if (this._enabled) {
      try { this._persistState(); } catch (e) { /* best-effort */ }
    }
    this._enabled = false;
    this._broadcastFare = false;
    this._stopTimer();
  }

  /** Restore running state from persisted data. */
  restoreFrom(saved) {
    this._sessionStart = saved.sessionStart;
    this._lastTimestamp = saved.lastTimestamp;
    this._totalJobDistance = saved.totalJobDistance;
    this._rawHaversineDistance = Number(saved.rawHaversineDistance) || 0;
    this._fareDistance = saved.fareDistance;
    this._fareTime = saved.fareTime;
    this._fare = saved.fare;
    this._waitTimeMs = saved.waitTimeMs || 0;
    this._lastLocation = saved.lastLocation;
    this._timeBufferCounter = saved.timeBufferCounter || 0;
    this._lastFixTimestamp = saved.lastFixTimestamp || 0;
    this._lastMotionBelowThreshold = !!saved.lastMotionBelowThreshold;
    this._lastSegmentStationaryForWaitTimer = !!saved.lastSegmentStationaryForWaitTimer;
    this._lastEffectiveSpeed = Number(saved.lastEffectiveSpeed) || 0;
    this._sessionFirstFixTs =
      saved.sessionFirstFixTs != null
        ? saved.sessionFirstFixTs
        : saved.lastTimestamp || saved.sessionStart || null;
    this._traceBuffer = Array.isArray(saved.traceBuffer) ? saved.traceBuffer.slice() : [];
    this._lastValidPointBeforeGap = saved.lastValidPointBeforeGap || null;
    this._gapPending = !!saved.gapPending;
    this._postGapAnchor = saved.postGapAnchor || null;
    this._lastEmittedOdometerMeters = null;
    // eslint-disable-next-line no-console
    console.log('[COLDSTART] softMeter.restoreFrom — gap state rehydrated', {
      gapPending: this._gapPending,
      lastValidPointBeforeGap: this._lastValidPointBeforeGap,
      postGapAnchor: this._postGapAnchor,
      lastLocation: this._lastLocation,
      lastFixTimestamp: this._lastFixTimestamp,
      traceBufferLen: this._traceBuffer.length,
    });
  }

  // ── internal ───────────────────────────────────────────────

  _calculateWaitCharge() {
    return computeWaitChargeForMs(this._waitTimeMs, this._waitConfig);
  }

  _incrementDistance(distanceMeters, timestamp) {
    this._fareDistance += distanceMeters;
    if (timestamp > this._lastTimestamp) this._lastTimestamp = timestamp;
    this._restartTimer();
  }

  _incrementTime(timestamp) {
    const raw = timestamp - this._lastTimestamp;
    const elapsed = Math.min(Math.max(0, raw), MAX_TICK_ACCRUE_MS);
    this._lastTimestamp = timestamp;
    this._fareTime += elapsed;
    this._restartTimer();
  }

  /**
   * 10s timer between GPS fixes. Wait + time fare accrue only while in time mode
   * (i.e. last segment was below threshold AND _timeBufferCounter > TIME_INTERVAL_MS).
   */
  _trackTime() {
    if (!this._lastMotionBelowThreshold || !this._lastSegmentStationaryForWaitTimer) return;
    const now = Date.now();
    const elapsed = Math.min(now - this._lastTimestamp, MAX_TICK_ACCRUE_MS);
    if (elapsed <= 0) return;

    // Wait accrues immediately on every below-threshold timer tick (no 10s gate). Time-mode
    // billing still requires the 10s buffer.
    this._waitTimeMs += elapsed;
    this._isWaiting = true;
    const inTimeMode = this._timeBufferCounter > TIME_INTERVAL_MS;
    if (inTimeMode) {
      this._timeBufferCounter += elapsed;
      this._incrementTime(now);
    } else {
      this._timeBufferCounter += elapsed;
      this._incrementDistance(0, now);
    }
  }

  _updateMeter(gpsSpeed, fareMode) {
    this.calculateFare(false);
    if (this._onMeterTick) {
      this._onMeterTick(this.getMeterStats(gpsSpeed));
    }
  }

  _emitFareUpdate() {
    if (this._onFareUpdate) {
      this._onFareUpdate(this._fare, this._fareMode, this.totalDistance, this._waitCharge);
    }
  }

  _startTimer() {
    this._stopTimer();
    this._timerHandle = setInterval(() => {
      if (this._lastLocation) {
        this._trackTime();
        this._updateMeter(0, 'time');
      }
    }, TIME_INTERVAL_MS);
  }

  _restartTimer() {
    this._startTimer();
  }

  _stopTimer() {
    if (this._timerHandle) {
      clearInterval(this._timerHandle);
      this._timerHandle = null;
    }
  }
}
